import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, CreditCard, Lock } from "lucide-react";
import { CreditCardAccountCard } from "./CreditCardAccountCard";
import { SortableCardWrapper } from "./SortableCardWrapper";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

interface CreditCardHorizontalScrollProps {
  creditCards: any[];
  onAddCard: () => void;
  onEditCard: (account: any) => void;
  onViewInvoice: (accountId: string) => void;
  onImport: (accountId: string) => void;
  onAddExpense: (accountId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  formatCurrency: (value: number) => string;
  refreshKey: number;
  locked?: boolean;
}

export function CreditCardHorizontalScroll({
  creditCards,
  onAddCard,
  onEditCard,
  onViewInvoice,
  onImport,
  onAddExpense,
  onDragEnd,
  formatCurrency,
  refreshKey,
  locked = false,
}: CreditCardHorizontalScrollProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (creditCards.length === 0) {
    return (
      <Card className="p-6 text-center">
        <CreditCard className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <h3 className="text-base font-semibold mb-1">Nenhum cartao cadastrado</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Adicione seus cartoes para controlar suas faturas
        </p>
        <Button variant="outline" size="sm" onClick={onAddCard}>
          {locked ? (
            <Lock className="h-4 w-4 mr-2 text-muted-foreground" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          Adicionar Cartao
        </Button>
      </Card>
    );
  }

  return (
    <>
      {/* Mobile: horizontal scroll */}
      <div className="md:hidden flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 -mx-1 px-1">
        {creditCards.map((account) => (
          <div key={account.id} className="snap-start shrink-0 w-[280px]">
            <CreditCardAccountCard
              account={account}
              onEdit={() => onEditCard(account)}
              onViewInvoice={() => onViewInvoice(account.id)}
              onImport={() => onImport(account.id)}
              onAddExpense={() => onAddExpense(account.id)}
              formatCurrency={formatCurrency}
              refreshKey={refreshKey}
            />
          </div>
        ))}
      </div>

      {/* Desktop: grid with drag and drop */}
      <div className="hidden md:block">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={creditCards.map(a => a.id)} strategy={rectSortingStrategy}>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {creditCards.map((account) => (
                <SortableCardWrapper key={account.id} id={account.id}>
                  <CreditCardAccountCard
                    account={account}
                    onEdit={() => onEditCard(account)}
                    onViewInvoice={() => onViewInvoice(account.id)}
                    onImport={() => onImport(account.id)}
                    onAddExpense={() => onAddExpense(account.id)}
                    formatCurrency={formatCurrency}
                    refreshKey={refreshKey}
                  />
                </SortableCardWrapper>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
}
